import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { EntityIntegration } from '@blesk/contracts';
import { apiPost } from '../../shared/api';
import { OUTBOX_COMMAND_LABEL, OUTBOX_STATUS_LABEL } from '../../shared/labels';
import { formatInProjectTz } from '../../shared/format';
import { useHasPermission } from '../../shared/session';
import { ErrorNote, orDash } from './ui';

/**
 * §13, §17.2 ТЗ, сценарий 34. Блок «Интеграция» в карточке инцидента и обращения.
 *
 * Показывает состояние команды в Outbox, число попыток и безопасное описание
 * последней ошибки (§18 — без токенов и текстов сообщений). Ручной повтор
 * доступен только `SYS_ADMIN` и не создаёт новый бизнес-объект: перезапускается
 * та же строка очереди.
 */
export function IntegrationBlock({
  integration,
  timezone,
  onRetried,
}: {
  integration: EntityIntegration | null;
  timezone: string;
  onRetried: () => void;
}) {
  const canRetry = useHasPermission('integration.retry');
  const queryClient = useQueryClient();

  const retry = useMutation({
    mutationFn: (outboxId: string) => apiPost(`/api/integration/outbox/${outboxId}/retry`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration'] });
      onRetried();
    },
  });

  if (!integration) {
    return (
      <div className="section-block">
        <h3>Интеграция</h3>
        <p className="muted">
          Команды в Bitrix24 нет. В автономном режиме это ожидаемо: событие сохранено, задача не
          создаётся.
        </p>
      </div>
    );
  }

  const badge =
    integration.status === 'DONE'
      ? 'badge--ok'
      : integration.status === 'PENDING' || integration.status === 'IN_PROGRESS'
        ? 'badge--warn'
        : 'badge--err';

  // §17.2: «требует сверки» из повторяемых исключено — результат прошлой
  // попытки неизвестен, и повтор вслепую запрещён. Такую команду разбирают
  // в разделе «Интеграции и журнал ошибок», а не отсюда.
  const retryable = integration.status === 'FAILED' || integration.status === 'NEEDS_ATTENTION';

  return (
    <div className="section-block">
      <h3>Интеграция</h3>
      <dl className="detail">
        <dt>Команда</dt>
        <dd>{OUTBOX_COMMAND_LABEL[integration.command] ?? integration.command}</dd>
        <dt>Статус</dt>
        <dd>
          <span className={`badge ${badge}`}>{OUTBOX_STATUS_LABEL[integration.status]}</span>
        </dd>
        {/* Счётчик ведёт только markFailed: «0» у выполненной команды — «прошло
            с первого раза», а не потерянная попытка (QA 22.08.2026, дефект 7) */}
        <dt>Неудачных попыток</dt>
        <dd>{integration.attempts}</dd>
        <dt>Следующая попытка</dt>
        <dd>{formatInProjectTz(integration.nextAttemptAt, timezone)}</dd>
        <dt>Обновлено</dt>
        <dd>{formatInProjectTz(integration.updatedAt, timezone)}</dd>
        <dt>Последняя ошибка</dt>
        <dd>{orDash(integration.lastError)}</dd>
      </dl>

      {integration.status === 'NEEDS_RECONCILIATION' && (
        <p className="muted">
          Результат внешней операции неизвестен, поэтому повторить её вслепую нельзя. Команду
          разбирают в разделе «Интеграции и журнал ошибок»: там отвечают, есть запись в Bitrix24 или
          нет.
        </p>
      )}

      <ErrorNote error={retry.error} />
      {canRetry && retryable && (
        <button
          type="button"
          className="btn-primary"
          disabled={retry.isPending}
          onClick={() => retry.mutate(integration.outboxId)}
        >
          Повторить операцию
        </button>
      )}
    </div>
  );
}
